// =====================================================================
// SESSION UTILITIES FOR FRONTEND
// File: src/utils/sessionUtils.js
// =====================================================================

import axios from 'axios';

const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

// Session inactivity timeout in milliseconds (60 minutes)
const SESSION_TIMEOUT_MS = 60 * 60 * 1000;
const LAST_ACTIVITY_KEY = 'lastActivity';

/**
 * Get the current session ID from localStorage
 * @returns {string|null} Session ID or null if not found
 */
export const getSessionId = () => {
  return localStorage.getItem('sessionId');
};

/**
 * Logout user and invalidate session on server
 * @returns {Promise<boolean>} Success status
 */
export const logout = async () => {
  const sessionId = getSessionId();
  
  if (!sessionId) {
    console.warn('No session ID found, clearing local storage only');
    clearLocalSession();
    return true;
  }
  
  try {
    const response = await axios.post(`${baseUrl}/api/users/logout`, {
      sessionId
    }, {
      withCredentials: true
    });
    
    if (response.data.success) {
      console.log('✅ Logout successful');
      clearLocalSession();
      return true;
    } else {
      console.warn('Logout response not successful:', response.data);
      clearLocalSession();
      return false;
    }
  } catch (error) {
    console.error('Error during logout:', error);
    // Still clear local session even if server request fails
    clearLocalSession();
    return false;
  }
};

/**
 * Clear all browser caches (Cache API used by the service worker).
 * Returns a promise; safe to call fire-and-forget.
 */
export const clearAppCaches = async () => {
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
      console.log('Cache cleared:', keys);
    }
  } catch (err) {
    console.warn('Failed to clear caches:', err);
  }
};

/**
 * Clear all session data from localStorage
 */
export const clearLocalSession = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('sessionId');
  localStorage.removeItem('tenantid');
  localStorage.removeItem('tenantId');
  localStorage.removeItem('ownerid');
  localStorage.removeItem('userId');
  localStorage.removeItem('username');
  localStorage.removeItem('role');
  localStorage.removeItem('me');
  localStorage.removeItem('user');
  localStorage.removeItem(LAST_ACTIVITY_KEY);

  // Clear window global if exists
  if (window.__ME__) {
    delete window.__ME__;
  }

  // Clear browser caches (fire-and-forget)
  clearAppCaches();
};

/**
 * Validate current session with server
 * @returns {Promise<{valid: boolean, user: object|null}>}
 */
export const validateSession = async () => {
  const sessionId = getSessionId();
  
  if (!sessionId) {
    return { valid: false, user: null };
  }
  
  try {
    const response = await axios.post(`${baseUrl}/api/users/validate-session`, {
      sessionId
    }, {
      withCredentials: true
    });
    
    if (response.data.valid) {
      return {
        valid: true,
        user: response.data.user
      };
    } else {
      // Session is invalid, clear local storage
      clearLocalSession();
      return { valid: false, user: null };
    }
  } catch (error) {
    console.error('Error validating session:', error);
    // On error, assume session is invalid
    clearLocalSession();
    return { valid: false, user: null };
  }
};

/**
 * Check if user is logged in (has valid session ID in localStorage)
 * Note: This only checks localStorage, not server validity
 * Use validateSession() for server-side validation
 * @returns {boolean}
 */
export const isLoggedIn = () => {
  return !!getSessionId();
};

/**
 * Get current user info from localStorage
 * @returns {object|null} User object or null
 */
export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
    return null;
  }
};

// ── Session Activity Tracking ──────────────────────────────────────────

/**
 * Update last-activity timestamp to now.
 * Called on user interactions (click, keypress, scroll, touch).
 */
export const touchActivity = () => {
  localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
};

/**
 * Returns true if the user has been inactive for >= SESSION_TIMEOUT_MS.
 * If there is no recorded activity (fresh login), returns false.
 */
export const isSessionTimedOut = () => {
  const last = localStorage.getItem(LAST_ACTIVITY_KEY);
  if (!last) return false;                        // no timestamp yet → not timed out
  return Date.now() - Number(last) >= SESSION_TIMEOUT_MS;
};

/**
 * Build the login redirect URL, preserving the user's current location
 * so they can be returned there after re-login.
 */
export const buildLoginRedirect = () => {
  const currentPath = window.location.pathname + window.location.search;
  // Don't save /login or /logout as return targets
  if (currentPath.startsWith('/login') || currentPath.startsWith('/logout')) {
    return '/login';
  }
  return `/login?returnTo=${encodeURIComponent(currentPath)}`;
};

/**
 * Setup axios interceptor to include session ID in requests
 * Call this in your app initialization (e.g., App.js useEffect)
 */
export const setupSessionInterceptor = () => {
  axios.interceptors.request.use(
    (config) => {
      const sessionId = getSessionId();
      if (sessionId) {
        // Add session ID to headers
        config.headers['X-Session-ID'] = sessionId;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle session expiration
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // If we get 401, session may be expired
      if (error.response?.status === 401) {
        clearLocalSession();
        window.location.href = buildLoginRedirect();
      }
      return Promise.reject(error);
    }
  );
};
